import React, { useState } from "react";
import { PlayCircle, Film } from "lucide-react";

/**
 * A small "real-world story" card shown at the top of every module.
 * Renders:
 *   • a short narrative (always),
 *   • optionally an embedded YouTube video (if `youtubeId` is set).
 */
export default function StoryCard({ story, youtubeId, moduleNumber }) {
  const [playVideo, setPlayVideo] = useState(false);

  if (!story && !youtubeId) return null;

  return (
    <div
      className="mb-12 rounded-md overflow-hidden border border-border bg-card"
      data-testid={`story-card-m${moduleNumber}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-5">
        {/* Video / thumbnail */}
        {youtubeId && (
          <div className="md:col-span-2 relative bg-black aspect-video md:aspect-auto md:min-h-[240px]">
            {playVideo ? (
              <iframe
                title={`Module ${moduleNumber} story video`}
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              />
            ) : (
              <button
                type="button"
                onClick={() => setPlayVideo(true)}
                data-testid={`story-play-btn-m${moduleNumber}`}
                className="group absolute inset-0 w-full h-full cursor-pointer"
                aria-label="Play real-world story video"
              >
                <img
                  src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`}
                  alt="Video thumbnail"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-95 transition-opacity"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/20 to-black/60" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                  <PlayCircle
                    size={64}
                    className="drop-shadow-lg group-hover:scale-110 transition-transform"
                  />
                  <span className="mt-3 text-xs tracking-[0.3em] uppercase opacity-90">
                    Watch · 30–60 sec
                  </span>
                </div>
              </button>
            )}
          </div>
        )}

        {/* Story text */}
        <div
          className={`${youtubeId ? "md:col-span-3" : "md:col-span-5"} p-6 md:p-8`}
        >
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-primary font-semibold">
            <Film size={12} /> A Real-World Story
          </div>
          <h3 className="font-serif text-xl md:text-2xl leading-snug tracking-tight mt-3 mb-4">
            Before we dive in…
          </h3>
          <div className="text-sm md:text-[15px] leading-relaxed text-foreground/90 space-y-3">
            {story.split("\n\n").map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
